import {Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AuthService} from './auth.service';
import {CommonUtils} from '../utils/common';
import {AppStateService} from './app-state.service';
import {SseNotification} from '../models/sse/sse-notification.model';
import {BookSseNotification} from '../models/sse/book-sse-notification.model';
import {BalanceSseNotification} from '../models/sse/balance-sse-notification.model';
import {VideoChatSseNotification} from '../models/sse/video-chat-sse-notification.model';
import {VetClinicInfoDataSyncNotification} from '../models/sse/vet-clinic-info-data-sync-notification.model';
import {VetClinicService} from "./vet-clinic.service";

/**
 * Serwis warstwy komunikacyjnej zarządzający połączeniami Server-Sent Events (SSE).
 * Zapewnia stały, jednokierunkowy strumień danych (Push) z serwera do klienta,
 * obsługując powiadomienia systemowe oraz synchronizację stanu aplikacji.
 */
@Injectable({
  providedIn: 'root'
})
export class SseService {
  /**
   * Instancja natywnego obiektu przeglądarki odpowiedzialnego za nasłuchiwanie strumienia.
   */
  private eventSource: EventSource;

  /**
   * Unikalny identyfikator aktualnej sesji połączeniowej.
   */
  public eventSourceConnectionId: string;

  /**
   * Główna szyna danych emitująca najnowsze powiadomienie SSE do subskrybujących komponentów.
   */
  public sseEvents = new BehaviorSubject<SseNotification>(null);

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    public commonUtils : CommonUtils,
    private appStateService: AppStateService,
    private vetClinicService: VetClinicService
  ) {}

  /**
   * Inicjuje lub wznawia główne połączenie SSE dla zalogowanego użytkownika w kontekście wybranej kliniki.
   *
   * @param userUUID Unikalny identyfikator zalogowanego użytkownika.
   * @param vetcodeId Identyfikator aktualnie wybranej kliniki.
   * @returns Observable emitujący powiadomienia spływające z serwera.
   */
  getClinicServerSentEvent(userUUID: string, vetcodeId: number): Observable<SseNotification> {
    if (!this.eventSourceConnectionId) { this.eventSourceConnectionId = this.commonUtils.generateUUID(); }
    return this.reconnectClinicServerSentEvent(this.eventSourceConnectionId, userUUID, vetcodeId);
  }

  /**
   * Tworzy całkowicie nowe połączenie strumieniowe, autoryzując się aktualnym tokenem JWT.
   */
  connectClinicServerSentEvent(userUUID: string, vetcodeId: number): Observable<any> {
    if (userUUID && vetcodeId) {
      return this.authService.userAuthData.pipe(take(1), switchMap(userAuthData => {
        if (userAuthData && userAuthData.token) {
          return this.getServerSentEvent(`${environment.VETINEO_SSE_URL}/connect/${vetcodeId}?token=${userAuthData.token}`);
        }
      }));
    }
  }

  /**
   * Wznawia istniejącą sesję SSE wykorzystując wygenerowany wcześniej Connection ID.
   */
  private reconnectClinicServerSentEvent(connectionId: string, userUUID: string, vetcodeId: number): Observable<SseNotification> {
    if (userUUID && vetcodeId) {
      return this.authService.userAuthData.pipe(take(1), switchMap(userAuthData => {
        if (userAuthData && userAuthData.token) {
          return this.getServerSentEvent(`${environment.VETINEO_SSE_URL}/reconnect/${connectionId}/${vetcodeId}?token=${userAuthData.token}`);
        }
      }));
    }
  }


  /**
   * Rdzeń systemu SSE. Tworzy natywny obiekt EventSource, nasłuchuje zdarzeń 'onmessage',
   * dekoduje ładunek JSON i rzutuje (mapuje) typy powiadomień na odpowiednie modele klas.
   *
   * @param url Kompletny adres URL endpointu strumieniowego wraz z tokenem autoryzacyjnym.
   * @returns Observable zarządzający cyklem życia połączenia.
   */
  getServerSentEvent(url: string): Observable<SseNotification> {
    return Observable.create(observer => {
      const eventSource = this.getEventSource(url);

      eventSource.onmessage = event => {
        if (event && event.data) {
          let messageDataObject;
          let syncClinicData = false;
          const messageData = JSON.parse(event.data);
          if (messageData.type && messageData.type === 'bookNotification' && messageData.data) {
            messageDataObject = this.commonUtils.bestTypedCopyEver(new BookSseNotification(), messageData.data);
            syncClinicData = true;
          }
          if (messageData.type && messageData.type === 'balanceRecalculatedNotification' && messageData.data) {
            messageDataObject = this.commonUtils.bestTypedCopyEver(new BalanceSseNotification(), messageData.data);
          }
          if (messageData.type && messageData.type === 'videoChatSseNotification' && messageData.data) {
            messageDataObject = this.commonUtils.bestTypedCopyEver(new VideoChatSseNotification(), messageData.data);
          }
          if (messageData.type && messageData.type === 'vetClinicInfoDataSync' && messageData.data) {
            messageDataObject = this.commonUtils.bestTypedCopyEver(new VetClinicInfoDataSyncNotification(), messageData.data);
          }
          if (messageDataObject) {
            if (syncClinicData) {
              this.vetClinicService.updateVetClinicDataRefresherSubject(true);
            }
            this.zone.run(() => {
              this.appStateService.sseConnectionDropped.next(false);
              observer.next(messageDataObject);
            });
          }
        }
      };

      eventSource.onerror = error => {
        this.zone.run(() => {
          this.appStateService.sseConnectionDropped.next(true);
          this.commonUtils.presentInfoToastIfNotExistent('Ładujemy dane, proszę czekać').then(observer.error(error));
        });
      };
    });
  }

  /**
   * Zamyka bezpiecznie aktywne połączenie strumieniowe i czyści identyfikator sesji.
   */
  public closeConnection() {
    if (this.eventSource) {
      this.eventSource.close();
    }

    this.eventSourceConnectionId = null;
  }

  /**
   * Implementacja wzorca Singleton dla instancji EventSource.
   * Gwarantuje, że nie powstaną zduplikowane połączenia pożerające zasoby sprzętowe i sieciowe.
   */
  private getEventSource(url: string): EventSource {
    if (!this.eventSource || this.eventSource.readyState === EventSource.CLOSED) {
      this.eventSource = new EventSource(url);
    }
    return this.eventSource;
  }
}
